/**
 * 栅格化图层（DESIGN.md §7.1 补充：图层面板右键"栅格化图层"，参照
 * Photoshop）。只对文字/形状图层可用——两者的落地方式差很大：
 *
 * - **文字图层**：排版结果早就栅格化写进了 `TileStore`（`text-tool.ts`
 *   的 `rasterizeText`），合成时也是直接按 `layer.id` 读 tile、不看类型
 *   （`composite.ts` 的 `compositeRaster` 对 `RasterLayer | TextLayer`
 *   一视同仁）。所以"栅格化文字图层"几乎是纯结构操作：零像素搬运，只是
 *   把 `TextLayer` 节点换成同 id 的 `RasterLayer` 节点。
 * - **形状图层**：纯矢量，合成时才现算（`composite.ts` 的
 *   `compositeShape`）。真的要跑一次这套算法，把结果写进这个图层 id
 *   名下新分配的 tiles（原来这个 id 在 TileStore 里什么都没有）。
 */
import { findLayer } from '../document/draft';
import type { Layer, Rect, RasterLayer } from '../document/types';
import { TILE, tileKey, type LayerId } from '../engine/tile/key';
import { BYTES_PER_TILE } from '../engine/tile/store';
import { renderShapeToImage, type CompositeResult } from './composite';
import { createRasterizeLayerCommand } from './commands/layer-commands';
import type { AppStore } from './store';

/** 已驻留 tile 的并集外接矩形（tile 网格对齐，不是精确像素边界）。无内容返回 null。 */
function tileAlignedBounds(store: AppStore, id: LayerId): Rect | null {
  let minTx = Infinity;
  let minTy = Infinity;
  let maxTx = -Infinity;
  let maxTy = -Infinity;
  for (const tile of store.tiles.residentTilesOf(id, 0)) {
    minTx = Math.min(minTx, tile.key.tx);
    minTy = Math.min(minTy, tile.key.ty);
    maxTx = Math.max(maxTx, tile.key.tx);
    maxTy = Math.max(maxTy, tile.key.ty);
  }
  if (!Number.isFinite(minTx)) return null;
  return {
    x: minTx * TILE,
    y: minTy * TILE,
    width: (maxTx - minTx + 1) * TILE,
    height: (maxTy - minTy + 1) * TILE,
  };
}

/**
 * 把一份跟文档等大的 RGBA8 图切成 tile 写进 TileStore（只写有内容的
 * tile），返回精确像素外接矩形（不是 tile 对齐的，用作新图层的 `bounds`）。
 * 导出给 `app/flatten.ts` 复用——"拼合图像"要做的事（把一份合成好的
 * RGBA 写进某个图层 id 名下新分配的 tiles）跟形状图层栅格化是同一个操作。
 */
export function writeImageIntoTiles(
  store: AppStore,
  id: LayerId,
  image: CompositeResult,
  transaction: ReturnType<AppStore['beginPixelTransaction']>,
): Rect {
  const tx1 = Math.max(0, Math.ceil(image.width / TILE) - 1);
  const ty1 = Math.max(0, Math.ceil(image.height / TILE) - 1);
  for (let ty = 0; ty <= ty1; ty++) {
    for (let tx = 0; tx <= tx1; tx++) transaction.touch(tileKey(id, tx, ty));
  }
  transaction.capture();

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let ty = 0; ty <= ty1; ty++) {
    for (let tx = 0; tx <= tx1; tx++) {
      const out = new Uint8Array(BYTES_PER_TILE);
      let hasInk = false;
      const rowTop = ty * TILE;
      const rowBottom = Math.min((ty + 1) * TILE, image.height);
      const colLeft = tx * TILE;
      const colRight = Math.min((tx + 1) * TILE, image.width);
      for (let y = rowTop; y < rowBottom; y++) {
        const srcStart = (y * image.width + colLeft) * 4;
        const length = (colRight - colLeft) * 4;
        const dstStart = ((y % TILE) * TILE + (colLeft % TILE)) * 4;
        out.set(image.pixels.subarray(srcStart, srcStart + length), dstStart);
        for (let x = colLeft; x < colRight; x++) {
          if (image.pixels[(y * image.width + x) * 4 + 3]! > 0) {
            hasInk = true;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
        }
      }
      if (hasInk) store.tiles.set(tileKey(id, tx, ty), out);
    }
  }

  return Number.isFinite(minX)
    ? { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 }
    : { x: 0, y: 0, width: 0, height: 0 };
}

/** 结构上原样保留 `LayerBase` 字段（可见性/不透明度/混合模式/蒙版/效果…），只换 kind 与像素归属。 */
function toRasterBase(layer: Layer, bounds: Rect, tiles: RasterLayer['tiles']): RasterLayer {
  return { ...layer, kind: 'raster', tiles, bounds };
}

/**
 * 栅格化一个图层。只对文字/形状图层生效（其余种类如实返回 false，
 * 右键菜单本身也只在这两种图层上出现这一项）。一次调用 = 一条历史。
 */
export function rasterizeLayer(store: AppStore, id: LayerId): boolean {
  const document = store.document;
  if (document === null) return false;
  const found = findLayer(document.root, id);
  if (found === null) return false;
  const layer = found.layer;

  if (layer.kind === 'text') {
    // 像素早就在 TileStore 里了（rasterizeText 写入时机见 text-tool.ts），
    // 零像素搬运，纯结构替换，不需要像素事务
    const bounds = tileAlignedBounds(store, id) ?? { x: 0, y: 0, width: 0, height: 0 };
    const raster = toRasterBase(layer, bounds, store.tiles.handle(id));
    store.dispatch(createRasterizeLayerCommand(id, raster));
    return true;
  }

  if (layer.kind === 'shape') {
    const image = renderShapeToImage(layer, document.size);
    const transaction = store.beginPixelTransaction('栅格化图层');
    const bounds = writeImageIntoTiles(store, id, image, transaction);
    const raster = toRasterBase(layer, bounds, store.tiles.handle(id));
    store.dispatchWithPixels(createRasterizeLayerCommand(id, raster), transaction.end());
    return true;
  }

  return false; // 其余种类（raster/group/adjustment/fill/smartObject）不需要栅格化
}
